import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Row, Col, Form, Button as BSButton, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';

import InputFile from '@app/components/input-file/InputFile';
import Editor from '@app/components/editor/Editor';
import { Get, Post, Put } from '@app/utils/httpProvider';
import { getInfomationAccountFromToken, getToken } from '@app/utils/helper';
import { URI_INTERNAL } from '@app/utils/pathLocations';
import { acceptImage, GIOI_TINH } from '@app/utils/define';
import { NHAN_VIEN_API, DON_VI_API, ACCOUNT_API, HOC_VI_API, CHUC_DANH_KHOA_HOC_API, CHUC_VU_API, BaseGatewayImage, FILE_MANAGER_API } from '@app/utils/apiUrl';
import HoSoDetail from './HoSoDetail';
import './NhanVien.scss';

const { Option } = Select;

// Yup Validation Schema with Vietnamese Messages
const schema = yup.object().shape({
    hoTen: yup.string().required('Họ và tên là bắt buộc'),
    ngaySinh: yup.date().required('Ngày sinh là bắt buộc').nullable(),
    gioiTinh: yup.number().required('Vui lòng chọn giới tính').nullable(),
    dienThoaiDiDong: yup.string()
        .nullable()
        .notRequired()
        .transform((curr, orig) => orig === '' ? null : curr)
        .test('phone-validation', 'Điện thoại chỉ chứa các chữ số và phải có ít nhất 10 số', value => {
            if (!value) return true;
            return /^[0-9]+$/.test(value) && value.length >= 10;
        }),
    emailCongViec: yup.string().email('Email không đúng định dạng').required('Email là bắt buộc').nullable(),
    soCMND_CCCD: yup.string().required('Số CMND/CCCD là bắt buộc'),
    chucVu: yup.string().required('Chức vụ là bắt buộc').nullable(),
    donViId: yup.string().required('Vui lòng chọn đơn vị công tác').nullable(),
    hocViCaoNhat: yup.number().required('Vui lòng chọn học vị cao nhất'),
    chucDanhKHCaoNhat: yup.number().required('Vui lòng chọn chức danh khoa học'),
    namChucDanh: yup.number().when('chucDanhKHCaoNhat', {
        is: (val) => val === 2 || val === 3,
        then: yup.number().required('Vui lòng nhập năm phong học hàm').nullable(),
        otherwise: yup.number().notRequired().nullable()
    }),
    noiBoNhiemChucDanh: yup.string().when('chucDanhKHCaoNhat', {
        is: (val) => val === 2 || val === 3,
        then: yup.string().required('Vui lòng nhập nơi phong học hàm').nullable(),
        otherwise: yup.string().notRequired().nullable()
    })
});

const NhanVienInput = () => {
    const history = useHistory();
    const { id: paramId } = useParams();
    const currentUser = getInfomationAccountFromToken();
    const isAdmin = currentUser?.role?.toLowerCase().includes('admin') || currentUser?.role?.toLowerCase().includes('publisher') || currentUser?.role?.toLowerCase().includes('manager');
    
    const [loading, setLoading] = useState(true);
    const [donVis, setDonVis] = useState([]);
    const [hocVis, setHocVis] = useState([]);
    const [chucDanhs, setChucDanhs] = useState([]);
    const [chucVus, setChucVus] = useState([]);
    const [nhanVienId, setNhanVienId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const triggerRefresh = () => setRefreshTrigger(prev => prev + 1);

    const [initialValues, setInitialValues] = useState({
        id: '',
        maNhanVien: '',
        hinhAnh: '',
        hoTen: '',
        ngaySinh: null,
        gioiTinh: '',
        dienThoaiDiDong: '',
        emailCongViec: '',
        soCMND_CCCD: '',
        soTaiKhoan: '',
        nganHang: '',
        maSoThue: '',
        chucVu: '',
        donViId: '',
        tenDonViTuNhap: '',
        hocViCaoNhat: '',
        chucDanhKHCaoNhat: '',
        namChucDanh: '',
        noiBoNhiemChucDanh: '',
        thongTinIdent: '',
        gioiThieuChung: '',
        chuyenNganhLinhVuc: '',
        kinhNghiemLamViec: '',
        congTrinhNBXB: '',
        thanhVienHoiNghe: '',
        giaiThuongDanhHieu: ''
    });

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                // 1. Tải danh sách khoa phòng đơn vị
                const unitRes = await Get(DON_VI_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 });
                const hvRes = await Get(HOC_VI_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 });
                const cdRes = await Get(CHUC_DANH_KHOA_HOC_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 });
                const cvRes = await Get(CHUC_VU_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 });

                const checkActive = (item) => {
                    if (!item) return false;
                    const val = item.isActive;
                    if (val === undefined || val === null) return false;
                    const str = String(val).trim().toLowerCase();
                    return str !== 'false' && str !== '0' && str !== 'null' && str !== 'undefined';
                };

                if (unitRes && unitRes.data) {
                    setDonVis(unitRes.data.filter(checkActive));
                }
                if (hvRes && hvRes.data) {
                    setHocVis(hvRes.data.filter(checkActive));
                }
                if (cdRes && cdRes.data) {
                    setChucDanhs(cdRes.data.filter(checkActive));
                }
                if (cvRes && cvRes.data) {
                    setChucVus(cvRes.data.filter(checkActive));
                }

                // 2. Kiểm tra thông tin nhân viên cần chỉnh sửa
                let targetNhanVienId = null;

                if (paramId && isAdmin) {
                    // Nếu là Admin và có paramId trên URL -> tải thông tin nhân viên được chọn
                    targetNhanVienId = paramId;
                } else if (currentUser && currentUser.id) {
                    // Nếu không thì tải thông tin nhân viên của tài khoản đang đăng nhập
                    const fullAccount = await Get(ACCOUNT_API.GET_BY_ID_ACCOUNT, currentUser.id);
                    if (fullAccount && fullAccount.nhanVienId) {
                        targetNhanVienId = fullAccount.nhanVienId;
                    } else {
                        // Nếu chưa có hồ sơ nhân viên liên kết, gán mặc định email từ account
                        setInitialValues(prev => ({
                            ...prev,
                            hoTen: fullAccount?.fullname || currentUser?.fullname || currentUser?.fullName || '',
                            emailCongViec: fullAccount?.email || currentUser?.email || '',
                            dienThoaiDiDong: fullAccount?.phoneNumber || currentUser?.phoneNumber || ''
                        }));
                    }
                }

                if (targetNhanVienId) {
                    // Tải thông tin chi tiết nhân viên
                    const empRes = await Get(NHAN_VIEN_API.GET_BY_ID, targetNhanVienId);
                    if (empRes && !empRes.error) {
                        setInitialValues({
                            ...empRes,
                            ngaySinh: empRes.ngaySinh ? dayjs(empRes.ngaySinh) : null
                        });
                        setNhanVienId(targetNhanVienId);
                    }
                }
            } catch (error) {
                console.error("Lỗi tải thông tin ban đầu:", error);
                toast.error("Không tải được dữ liệu nhân viên");
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, []);

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const payload = {
                ...values,
                ngaySinh: values.ngaySinh ? dayjs(values.ngaySinh).format('YYYY-MM-DD') : null
            };

            if (values.id) {
                // CẬP NHẬT HỒ SƠ
                const res = await Put(NHAN_VIEN_API.EDIT_ITEM, values.id, payload);
                if (res && !res.error) {
                    toast.success("Cập nhật thông tin nhân viên thành công!");
                } else {
                    toast.error("Cập nhật thông tin thất bại!");
                }
            } else {
                // KHỞI TẠO MỚI HỒ SƠ
                const res = await Post(NHAN_VIEN_API.ADD_ITEM, null, payload, false);
                if (res && res.id) {
                    // Liên kết tài khoản hiện tại với nhân viên vừa tạo
                    const fullAccount = await Get(ACCOUNT_API.GET_BY_ID_ACCOUNT, currentUser.id);
                    if (fullAccount) {
                        fullAccount.nhanVienId = res.id;
                        if (typeof fullAccount.roles === 'string') {
                            fullAccount.roles = fullAccount.roles.split(',').map(r => r.trim()).filter(Boolean);
                        } else if (!Array.isArray(fullAccount.roles)) {
                            fullAccount.roles = [];
                        }
                        await Put(ACCOUNT_API.EDIT_ACCOUNT, currentUser.id, fullAccount);
                    }

                    setNhanVienId(res.id);
                    setInitialValues(prev => ({ ...prev, id: res.id }));
                    toast.success("Khởi tạo hồ sơ nhân viên thành công! Bạn có thể khai báo thành tích ngay bên dưới.");
                } else {
                    toast.error("Khởi tạo hồ sơ thất bại!");
                }
            }
        } catch (error) {
            console.error("Lỗi khi lưu thông tin nhân viên:", error);
            toast.error("Đã xảy ra lỗi hệ thống khi lưu thông tin");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ml-2">Đang tải biểu mẫu nhân viên...</span>
            </div>
        );
    }

    return (
        <div className="container-fluid pb-4 px-3">
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={schema}
                onSubmit={handleSubmit}
            >
                {({
                    handleSubmit,
                    handleChange,
                    values,
                    errors,
                    touched,
                    setFieldValue,
                    isSubmitting
                }) => {
                    const handleAvatarChange = async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        if (!file.type.startsWith('image/')) {
                            toast.error("Vui lòng chọn file hình ảnh (jpg, png, jpeg...)");
                            return;
                        }

                        setUploading(true);
                        const token = getToken();
                        const config = {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                Authorization: `bearer ${token}`
                            }
                        };

                        const data = new FormData();
                        data.append('file', file);

                        try {
                            const res = await axios.post(FILE_MANAGER_API.UPLOAD, data, config);
                            let fileUpdated = null;
                            if (res && res.data) {
                                if (Array.isArray(res.data)) {
                                    fileUpdated = res.data[0];
                                } else if (res.data.$values && Array.isArray(res.data.$values)) {
                                    fileUpdated = res.data.$values[0];
                                } else {
                                    fileUpdated = res.data;
                                }
                            }

                            if (fileUpdated && (fileUpdated.filePath || fileUpdated.FilePath)) {
                                const filePath = fileUpdated.filePath || fileUpdated.FilePath;
                                setFieldValue('hinhAnh', filePath);
                                toast.success("Tải ảnh đại diện lên thành công!");
                            } else {
                                toast.error("Tải ảnh đại diện thất bại!");
                            }
                        } catch (error) {
                            console.error("Lỗi upload avatar:", error);
                            toast.error("Lỗi khi tải ảnh lên server");
                        } finally {
                            setUploading(false);
                            if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                            }
                        }
                    };

                    return (
                        <Form noValidate onSubmit={handleSubmit}>
                        <Card className="premium-form-card">
                            <Card.Header style={{ backgroundColor: '#28a745' }} className="text-white">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h3 className="mb-1 font-weight-bold" style={{ letterSpacing: '0.5px' }} >
                                            <i className="fas fa-user-edit mr-2" />
                                            {values.id ? "Cập Nhật Hồ Sơ Nhân Viên" : "Khai Báo Lý Lịch Nhân Viên Mới"}
                                        </h3>
                                        <p className="mb-0 text-white" style={{ fontSize: '14px' }}>
                                            Khai báo đầy đủ các trường thông tin cá nhân bắt buộc phục vụ in lý lịch khoa học (Phụ lục 02).
                                        </p>
                                    </div>
                                    <BSButton 
                                        variant="light" 
                                        onClick={() => history.push(URI_INTERNAL.PROFILE_CV)}
                                        style={{ borderRadius: '8px', fontWeight: 600 }}
                                    >
                                        <i className="fas fa-arrow-left mr-2" />Trở lại
                                    </BSButton>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4 px-md-5">
                                
                                {/* PHẦN 1: THÔNG TIN CƠ BẢN */}
                                <div className="form-section-title">
                                    <i className="fas fa-id-card" />
                                    I. Thông tin cá nhân cơ bản
                                </div>
                                <Row className="mb-4">
                                    {/* Ảnh đại diện */}
                                    <Col lg={3} md={4} className="mb-3 d-flex flex-column align-items-center border-right">
                                        <div className="form-label-custom text-center mb-2">Ảnh đại diện (Avatar)</div>
                                        <div className="d-flex flex-column align-items-center">
                                            <div className="position-relative mb-3">
                                                <div className="avatar-preview-box">
                                                    {uploading ? (
                                                        <div className="d-flex flex-column align-items-center justify-content-center">
                                                            <Spinner animation="border" variant="primary" size="sm" />
                                                            <span className="mt-1 text-muted" style={{ fontSize: '11px' }}>Đang tải...</span>
                                                        </div>
                                                    ) : values.hinhAnh ? (
                                                        <img 
                                                            src={values.hinhAnh.startsWith('http') ? values.hinhAnh : BaseGatewayImage + (values.hinhAnh.startsWith('/') ? '' : '/') + values.hinhAnh} 
                                                            alt="Avatar Preview" 
                                                            className="avatar-preview-img"
                                                        />
                                                    ) : (
                                                        <div className="text-center p-3 text-muted">
                                                            <i className="fas fa-user-circle fa-3x mb-2 text-white-50" style={{ color: '#d9d9d9' }} />
                                                            <div style={{ fontSize: '11px' }}>Chưa có ảnh</div>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Nút xóa ảnh nằm bên ngoài, tuyệt đối không bị overflow: hidden của avatar-preview-box cắt đi */}
                                                {values.hinhAnh && !uploading && (
                                                    <div 
                                                        className="position-absolute d-flex align-items-center justify-content-center bg-danger text-white rounded-circle"
                                                        style={{ 
                                                            top: '-4px', 
                                                            right: '-4px', 
                                                            width: '26px', 
                                                            height: '26px', 
                                                            cursor: 'pointer',
                                                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                                            zIndex: 10,
                                                            border: '2px solid #ffffff'
                                                        }}
                                                        onClick={() => setFieldValue('hinhAnh', '')}
                                                        title="Xóa ảnh đại diện"
                                                    >
                                                        <i className="fas fa-times" style={{ fontSize: '12px' }} />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Nút chọn ảnh / tải ảnh lên */}
                                            <input 
                                                type="file" 
                                                ref={fileInputRef} 
                                                style={{ display: 'none' }} 
                                                accept={acceptImage} 
                                                onChange={handleAvatarChange} 
                                            />
                                            <BSButton 
                                                variant={values.hinhAnh ? "outline-primary" : "primary"}
                                                size="sm"
                                                style={{ borderRadius: '20px', padding: '6px 16px', fontWeight: 600 }}
                                                onClick={() => fileInputRef.current.click()}
                                                disabled={uploading}
                                            >
                                                {uploading ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="mr-1" />
                                                        Đang tải...
                                                    </>
                                                ) : values.hinhAnh ? (
                                                    <>
                                                        <i className="fas fa-sync-alt mr-1" /> Thay đổi ảnh
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-camera mr-1" /> Tải ảnh lên
                                                    </>
                                                )}
                                            </BSButton>
                                        </div>
                                    </Col>

                                    {/* Các trường cơ bản bên phải */}
                                    <Col lg={9} md={8}>
                                        <Row>
                                            <Col md={6} className="mb-3">
                                                <Form.Label className="form-label-custom">Họ và tên (*)</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="hoTen"
                                                    placeholder="Nhập họ và tên giảng viên / nhân viên"
                                                    className="form-control-custom"
                                                    value={values.hoTen}
                                                    onChange={handleChange}
                                                    isInvalid={touched.hoTen && !!errors.hoTen}
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.hoTen}</Form.Control.Feedback>
                                            </Col>

                                            <Col md={3} className="mb-3">
                                                <Form.Label className="form-label-custom">Mã nhân viên (nếu có)</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="maNhanVien"
                                                    placeholder="Ví dụ: NV108"
                                                    className="form-control-custom"
                                                    value={values.maNhanVien}
                                                    onChange={handleChange}
                                                />
                                            </Col>

                                            <Col md={3} className="mb-3">
                                                <Form.Label className="form-label-custom">Giới tính (*)</Form.Label>
                                                <Select
                                                    placeholder="Giới tính"
                                                    value={values.gioiTinh || undefined}
                                                    onChange={(val) => setFieldValue('gioiTinh', val)}
                                                    className="w-100"
                                                    style={{ height: '40px' }}
                                                    status={touched.gioiTinh && errors.gioiTinh ? "error" : ""}
                                                >
                                                    {GIOI_TINH.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                                                </Select>
                                                {touched.gioiTinh && errors.gioiTinh && (
                                                    <div className="text-danger mt-1" style={{ fontSize: '80%' }}>{errors.gioiTinh}</div>
                                                )}
                                            </Col>

                                            <Col md={6} className="mb-3">
                                                <Form.Label className="form-label-custom">Ngày sinh (*)</Form.Label>
                                                <div>
                                                    <DatePicker
                                                        format="DD/MM/YYYY"
                                                        value={values.ngaySinh}
                                                        onChange={(val) => setFieldValue('ngaySinh', val)}
                                                        className="w-100"
                                                        style={{ height: '40px', borderRadius: '8px' }}
                                                        placeholder="Chọn ngày sinh"
                                                        status={touched.ngaySinh && errors.ngaySinh ? "error" : ""}
                                                    />
                                                </div>
                                                {touched.ngaySinh && errors.ngaySinh && (
                                                    <div className="text-danger mt-1" style={{ fontSize: '80%' }}>{errors.ngaySinh}</div>
                                                )}
                                            </Col>

                                            <Col md={6} className="mb-3">
                                                <Form.Label className="form-label-custom">Số CMND / Căn cước công dân (*)</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="soCMND_CCCD"
                                                    placeholder="Số CMND hoặc CCCD 12 số"
                                                    className="form-control-custom"
                                                    value={values.soCMND_CCCD}
                                                    onChange={handleChange}
                                                    isInvalid={touched.soCMND_CCCD && !!errors.soCMND_CCCD}
                                                />
                                                <Form.Control.Feedback type="invalid">{errors.soCMND_CCCD}</Form.Control.Feedback>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                {/* PHẦN 2: THÔNG TIN LIÊN HỆ & TÀI CHÍNH */}
                                <div className="form-section-title mt-4">
                                    <i className="fas fa-phone-alt" />
                                    II. Liên hệ & Tài chính
                                </div>
                                <Row className="mb-4">
                                    <Col md={6} className="mb-3">
                                        <Form.Label className="form-label-custom">Điện thoại di động</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="dienThoaiDiDong"
                                            placeholder="Số điện thoại di động cá nhân"
                                            className="form-control-custom"
                                            value={values.dienThoaiDiDong}
                                            onChange={handleChange}
                                            isInvalid={touched.dienThoaiDiDong && !!errors.dienThoaiDiDong}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.dienThoaiDiDong}</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6} className="mb-3">
                                        <Form.Label className="form-label-custom">Email công việc (*)</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="emailCongViec"
                                            placeholder="Địa chỉ email liên hệ công việc"
                                            className="form-control-custom"
                                            value={values.emailCongViec}
                                            onChange={handleChange}
                                            isInvalid={touched.emailCongViec && !!errors.emailCongViec}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.emailCongViec}</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4} className="mb-3">
                                        <Form.Label className="form-label-custom">Số tài khoản ngân hàng</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="soTaiKhoan"
                                            placeholder="Nhập số tài khoản"
                                            className="form-control-custom"
                                            value={values.soTaiKhoan}
                                            onChange={handleChange}
                                        />
                                    </Col>

                                    <Col md={4} className="mb-3">
                                        <Form.Label className="form-label-custom">Tại ngân hàng</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="nganHang"
                                            placeholder="Tên ngân hàng mở tài khoản"
                                            className="form-control-custom"
                                            value={values.nganHang}
                                            onChange={handleChange}
                                        />
                                    </Col>

                                    <Col md={4} className="mb-3">
                                        <Form.Label className="form-label-custom">Mã số thuế cá nhân</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="maSoThue"
                                            placeholder="Nhập mã số thuế"
                                            className="form-control-custom"
                                            value={values.maSoThue}
                                            onChange={handleChange}
                                        />
                                    </Col>
                                </Row>

                                {/* PHẦN 3: ĐƠN VỊ CÔNG TÁC & HỌC VỊ */}
                                <div className="form-section-title mt-4">
                                    <i className="fas fa-university" />
                                    III. Đơn vị công tác & Học hàm học vị
                                </div>
                                <Row className="mb-4">
                                    <Col md={4} className="mb-3">
                                        <Form.Label className="form-label-custom">Chức vụ công tác (*)</Form.Label>
                                        <Select
                                            placeholder="Chọn chức vụ công tác"
                                            value={values.chucVu || undefined}
                                            onChange={(val) => setFieldValue('chucVu', val)}
                                            className="w-100"
                                            style={{ height: '40px' }}
                                            status={touched.chucVu && errors.chucVu ? "error" : ""}
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {chucVus.map(o => (
                                                <Option key={o.tenChucVu} value={o.tenChucVu}>{o.tenChucVu}</Option>
                                            ))}
                                        </Select>
                                        {touched.chucVu && errors.chucVu && (
                                            <div className="text-danger mt-1" style={{ fontSize: '80%' }}>{errors.chucVu}</div>
                                        )}
                                    </Col>

                                    <Col md={4} className="mb-3">
                                        <Form.Label className="form-label-custom">Đơn vị khoa / phòng (*)</Form.Label>
                                        <Select
                                            placeholder="Chọn khoa phòng công tác"
                                            value={values.donViId || undefined}
                                            onChange={(val) => setFieldValue('donViId', val)}
                                            className="w-100"
                                            style={{ height: '40px' }}
                                            status={touched.donViId && errors.donViId ? "error" : ""}
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {donVis.map(u => (
                                                <Option key={u.id} value={u.id}>{u.tenDonVi}</Option>
                                            ))}
                                        </Select>
                                        {touched.donViId && errors.donViId && (
                                            <div className="text-danger mt-1" style={{ fontSize: '80%' }}>{errors.donViId}</div>
                                        )}
                                    </Col>

                                    <Col md={4} className="mb-3">
                                        <Form.Label className="form-label-custom">Đơn vị khác (nếu tự nhập)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="tenDonViTuNhap"
                                            placeholder="Nhập tên đơn vị nếu chưa có ở danh sách"
                                            className="form-control-custom"
                                            value={values.tenDonViTuNhap}
                                            onChange={handleChange}
                                        />
                                    </Col>

                                    <Col md={6} className="mb-3">
                                        <Form.Label className="form-label-custom">Học vị cao nhất (*)</Form.Label>
                                        <Select
                                            placeholder="Chọn học vị cao nhất"
                                            value={values.hocViCaoNhat || undefined}
                                            onChange={(val) => setFieldValue('hocViCaoNhat', val)}
                                            className="w-100"
                                            style={{ height: '40px' }}
                                            status={touched.hocViCaoNhat && errors.hocViCaoNhat ? "error" : ""}
                                        >
                                            {hocVis.map(o => <Option key={o.id} value={o.id}>{o.tenHocVi}</Option>)}
                                        </Select>
                                        {touched.hocViCaoNhat && errors.hocViCaoNhat && (
                                            <div className="text-danger mt-1" style={{ fontSize: '80%' }}>{errors.hocViCaoNhat}</div>
                                        )}
                                    </Col>

                                    <Col md={4} className="mb-3">
                                        <Form.Label className="form-label-custom">Chức danh Giáo sư hoặc Phó giáo sư*</Form.Label>
                                        <Select
                                            placeholder="Chọn chức danh khoa học"
                                            value={values.chucDanhKHCaoNhat !== undefined ? values.chucDanhKHCaoNhat : undefined}
                                            onChange={(val) => {
                                                setFieldValue('chucDanhKHCaoNhat', val);
                                                if (val === 1) { // 1 là ID của "Không"
                                                    setFieldValue('namChucDanh', '');
                                                    setFieldValue('noiBoNhiemChucDanh', '');
                                                }
                                            }}
                                            className="w-100"
                                            style={{ height: '40px' }}
                                            status={touched.chucDanhKHCaoNhat && errors.chucDanhKHCaoNhat ? "error" : ""}
                                        >
                                            {chucDanhs.map(o => <Option key={o.id} value={o.id}>{o.tenChucDanh}</Option>)}
                                        </Select>
                                        {touched.chucDanhKHCaoNhat && errors.chucDanhKHCaoNhat && (
                                            <div className="text-danger mt-1" style={{ fontSize: '80%' }}>{errors.chucDanhKHCaoNhat}</div>
                                        )}
                                    </Col>

                                    <Col md={4} className="mb-3">
                                        <Form.Label className="form-label-custom">Năm*</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="namChucDanh"
                                            placeholder="Năm phong tặng"
                                            className="form-control-custom"
                                            value={values.namChucDanh || ''}
                                            onChange={handleChange}
                                            disabled={values.chucDanhKHCaoNhat === 1 || !values.chucDanhKHCaoNhat}
                                            isInvalid={touched.namChucDanh && !!errors.namChucDanh}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.namChucDanh}</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4} className="mb-3">
                                        <Form.Label className="form-label-custom">Nơi bổ nhiệm*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="noiBoNhiemChucDanh"
                                            placeholder="Nơi bổ nhiệm"
                                            className="form-control-custom"
                                            value={values.noiBoNhiemChucDanh || ''}
                                            onChange={handleChange}
                                            disabled={values.chucDanhKHCaoNhat === 1 || !values.chucDanhKHCaoNhat}
                                            isInvalid={touched.noiBoNhiemChucDanh && !!errors.noiBoNhiemChucDanh}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.noiBoNhiemChucDanh}</Form.Control.Feedback>
                                    </Col>

                                    <Col md={12} className="mb-3">
                                        <Form.Label className="form-label-custom">Các thông tin về chỉ số định danh ORCID, hồ sơ Google scholar, H-index, số lượt trích dẫn (nếu có)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="thongTinIdent"
                                            placeholder="Nhập thông tin ORCID, Google Scholar, H-Index..."
                                            className="form-control-custom"
                                            value={values.thongTinIdent || ''}
                                            onChange={handleChange}
                                        />
                                    </Col>
                                </Row>

                                {/* QUÁ TRÌNH ĐÀO TẠO & NGOẠI NGỮ */}
                                {nhanVienId && (
                                    <div className="mb-4">
                                        <div className="form-section-title mt-4">
                                            <i className="fas fa-graduation-cap" />
                                            Quá trình đào tạo & Chứng chỉ ngoại ngữ
                                        </div>
                                        <HoSoDetail 
                                            nhanVienIdProp={nhanVienId} 
                                            onlyEducationAndLanguage={true} 
                                            onRefresh={triggerRefresh} 
                                            refreshTrigger={refreshTrigger} 
                                        />
                                    </div>
                                )}

                                {/* PHẦN 4: GIỚI THIỆU BẢN THÂN */}
                                <div className="form-section-title mt-4">
                                    <i className="fas fa-align-left" />
                                    IV. Giới thiệu bản thân & Hướng nghiên cứu khoa học
                                </div>
                                <Row>
                                    <Col md={12} className="mb-4">
                                        <Form.Label className="form-label-custom">Giới thiệu chung</Form.Label>
                                        <Editor
                                            name="gioiThieuChung"
                                            value={values.gioiThieuChung}
                                            onChange={(val) => setFieldValue('gioiThieuChung', val)}
                                        />
                                    </Col>

                                    <Col md={12} className="mb-4">
                                        <Form.Label className="form-label-custom">Chuyên ngành, thế mạnh chuyên môn</Form.Label>
                                        <Editor
                                            name="chuyenNganhLinhVuc"
                                            value={values.chuyenNganhLinhVuc}
                                            onChange={(val) => setFieldValue('chuyenNganhLinhVuc', val)}
                                        />
                                    </Col>

                                    <Col md={12} className="mb-4">
                                        <Form.Label className="form-label-custom">Kinh nghiệm làm việc</Form.Label>
                                        <Editor
                                            name="kinhNghiemLamViec"
                                            value={values.kinhNghiemLamViec}
                                            onChange={(val) => setFieldValue('kinhNghiemLamViec', val)}
                                        />
                                    </Col>

                                    <Col md={12} className="mb-4">
                                        <Form.Label className="form-label-custom">Công trình nghiên cứu, xuất bản và đào tạo</Form.Label>
                                        <Editor
                                            name="congTrinhNBXB"
                                            value={values.congTrinhNBXB}
                                            onChange={(val) => setFieldValue('congTrinhNBXB', val)}
                                        />
                                    </Col>

                                    <Col md={12} className="mb-4">
                                        <Form.Label className="form-label-custom">Thành viên Hội nghề nghiệp</Form.Label>
                                        <Editor
                                            name="thanhVienHoiNghe"
                                            value={values.thanhVienHoiNghe}
                                            onChange={(val) => setFieldValue('thanhVienHoiNghe', val)}
                                        />
                                    </Col>

                                    <Col md={12} className="mb-4">
                                        <Form.Label className="form-label-custom">Giải thưởng và danh hiệu</Form.Label>
                                        <Editor
                                            name="giaiThuongDanhHieu"
                                            value={values.giaiThuongDanhHieu}
                                            onChange={(val) => setFieldValue('giaiThuongDanhHieu', val)}
                                        />
                                    </Col>
                                </Row>

                                {/* NÚT HÀNH ĐỘNG */}
                                <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                                    <BSButton 
                                        type="button" 
                                        variant="light" 
                                        onClick={() => history.push(URI_INTERNAL.PROFILE_CV)}
                                        className="btn-premium-cancel mr-2"
                                    >
                                        Hủy bỏ
                                    </BSButton>
                                    <BSButton 
                                        type="submit" 
                                        className="btn-premium-save" 
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="mr-2" />
                                                Đang xử lý...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save mr-2" />
                                                Lưu hồ sơ lý lịch
                                            </>
                                        )}
                                    </BSButton>
                                </div>

                            </Card.Body>
                        </Card>
                    </Form>
                    );
                }}
            </Formik>

            {/* PHẦN KHAI BÁO THÀNH TÍCH — Nhúng HoSoDetail khi đã có hồ sơ */}
            {nhanVienId && (
                <div className="mt-4">
                    <HoSoDetail 
                        nhanVienIdProp={nhanVienId} 
                        hideEducationAndLanguage={true} 
                        onRefresh={triggerRefresh} 
                        refreshTrigger={refreshTrigger} 
                    />
                </div>
            )}
            
        </div>
    );
};

export default NhanVienInput;
